import React, { useCallback, useMemo, useState } from "react"

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core"

import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable"

import {
  DragHandle,
  SortableItem,
  SortableOverlay
} from "./components"

function HKVSortableListComponent({ items, onChange, renderItem }) {
  const [active, setActive] = useState(null)
  const activeItem = useMemo(
    () => items.find((item) => item.id === active?.id),
    [active, items]
  )
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragStart = useCallback((event) => {
    event?.event?.stopPropagation?.()
    event?.activatorEvent?.stopPropagation?.()
    setActive(event.active)
  }, [])

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (over && active.id !== over?.id) {
        const activeIndex = items.findIndex(({ id }) => id === active.id)
        const overIndex = items.findIndex(({ id }) => id === over.id)
        onChange(arrayMove(items, activeIndex, overIndex))
      }
      setActive(null)
    },
    [items, onChange]
  )

  const handleDragCancel = useCallback(() => setActive(null), [])

  return (
    <DndContext
      collisionDetection={closestCenter}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={items}
        strategy={verticalListSortingStrategy}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>{renderItem(item, index)}</React.Fragment>
          ))}
        </div>
      </SortableContext>
      <SortableOverlay>
        {activeItem ? renderItem(activeItem) : null}
      </SortableOverlay>
    </DndContext>
  )
}

export const HKVSortableList = React.memo(HKVSortableListComponent)

HKVSortableList.Item = SortableItem
HKVSortableList.DragHandle = DragHandle